import { AfterViewInit, Component, OnInit, Input } from '@angular/core';
import { TslSort, PageEvent, TslDialog } from '@tesla/ng-tds';
import { MatDialog } from '@angular/material/dialog';
import { SupplierDocumentService } from 'app/profile/services/supplier-document.service';
import { SupplierDocumentPropertyValues, DocumentType, TabPermissions, SupplierPropertyValue, OPERATION, Supplier, SupplierDocument, SupplierContactPropertyValue } from 'app/profile/models/supplier.model';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { Store, select } from '@ngrx/store';
import { GetSupplierDocumentsListByCode, GetTabPermissions, PostEvent, ClearSupplierDetailsByCode } from 'app/profile/store/app.actions';
import { SelectSupplierDocumentList, SelectTabPermissions, SelectEventData, SelectCurrentUser, SelectSupplierContactList } from 'app/profile/store/app.selector';
import { BehaviorSubject } from 'rxjs';
import { DocumentDialogComponent } from '../document-dialog/document-dialog.component';
import { I18nService } from 'app/shared/services/i18n.service';
import { getNestedNode, showMFADialog } from 'app/shared/utils/utils';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import * as FileSaver from 'file-saver';
import { MfaBannerComponent } from '../../mfa-banner/mfa-banner.component';
import { UserDetails } from 'app/profile/models/user.model';
import { AuthData } from '@tesla/ng-auth';

@Component({
    selector: 'app-document-grid',
    templateUrl: './document-grid.component.html',
    styleUrls: ['./document-grid.component.scss'],
})
export class DocumentGridComponent implements AfterViewInit, OnInit {
    @Input() supplierCode: string;
    datasource: BehaviorSubject<Array<SupplierDocumentPropertyValues>> = new BehaviorSubject([]);
    allDocuments: SupplierDocumentPropertyValues[] = [];
    types: DocumentType[] = [];
    categories: string[] = [];
    selectedCategory: string;
    deleteTitle: string;
    deleteConfirmation: string;
    showMFADialog: boolean = false;
    userDetails: UserDetails;
    supplierContacts: SupplierContactPropertyValue[] = [];

    tabPermissions: TabPermissions;

    /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
    displayedColumns = ['category', 'name', 'start', 'expiry', 'signature', 'action'];
    pageSizeOptions: number[] = [10, 25, 50, 100, 250];
    pageIndex = 0;
    pageSize = 10;
    total = 0;

    get hasWriteAccess(): boolean {
        return this.tabPermissions && this.tabPermissions.document === 'ReadWrite';
    }

    constructor(
        private supplierStateService: SupplierStateService,
        public translateService: I18nService,
        private documentService: SupplierDocumentService,
        private store: Store<SupplierState>,
        private dialog: TslDialog,
        private authData: AuthData,
        private matDialog: MatDialog,
    ) {}

    ngOnInit() {
        if (this.supplierCode) {
            this.loadResources();
        }

        this.store.pipe(select(SelectCurrentUser)).subscribe((result) => {
            if (result) {
                this.userDetails = result;
                this.showMFADialog = showMFADialog(this.authData, result);
            }
        });

        this.store.pipe(select(SelectSupplierContactList)).subscribe(res => {
            if(res){
                this.supplierContacts = res;
            }
        })
    }

    ngAfterViewInit() {}

    onSortChange(sort: TslSort): void {
        const orderedItems = this.allDocuments.sort((a, b) => {
            const isAsc = sort.direction === 'asc';

            switch (sort.active) {
                case 'category':
                    return compare(a.documentType.newValue.toLowerCase(), b.documentType.newValue.toLowerCase(), isAsc);
                case 'name':
                    return compare(a.fileName.newValue.toLowerCase(), b.fileName.newValue.toLowerCase(), isAsc);
                case 'start': {
                    const aDate = new Date(a.effectiveDate.newValue);
                    const bDate = new Date(b.effectiveDate.newValue);
                    return compare(aDate.getTime(), bDate.getTime(), isAsc);
                }
                case 'expiry': {
                    const aDate = new Date(a.expiryDate.newValue);
                    const bDate = new Date(b.expiryDate.newValue);
                    return compare(aDate.getTime(), bDate.getTime(), isAsc);
                }
                case 'signature': {
                    const aDate = new Date(a.expiryDate.newValue);
                    const bDate = new Date(b.expiryDate.newValue);
                    return compare(aDate.getTime(), bDate.getTime(), isAsc);
                }
            }
        });

        const skip = this.pageIndex * this.pageSize;
        const take = (this.pageIndex + 1) * this.pageSize;
        this.datasource.next(orderedItems.slice(skip, take));

        function compare(a: number | string, b: number | string, isAsc: boolean) {
            return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
        }
    }

    onPageChange(pageChange: PageEvent): void {
        const { pageIndex, pageSize } = pageChange;
        this.pageIndex = pageIndex;
        this.pageSize = pageSize;
        this.onFilter();
    }

    onFilter(): void {
        let items = this.allDocuments;

        if (this.selectedCategory) {
            const types = this.types.filter((t) => t.category === this.selectedCategory).map((x) => x.name);
            items = items.filter((x) => types.includes(x.documentType.newValue));
        }

        const skip = this.pageIndex * this.pageSize;
        const take = (this.pageIndex + 1) * this.pageSize;
        this.datasource.next(items.slice(skip, take));
    }

    onClearFilter(): void {
        this.selectedCategory = '';
        this.onFilter();
    }

    onDownloadFile(document: SupplierDocumentPropertyValues) {
        if (document) {
            this.supplierStateService
                .downloadFile(
                    this.supplierCode,
                    document.referenceSystem.newValue,
                    document.documentID.newValue,
                    document.documentVersion.newValue,
                )
                .subscribe((file) => {
                    const fileName: string = document.fileName.newValue;
                    FileSaver.saveAs(file, fileName);
                });
        }
    }

    onAdd(): void {
        if (this.showMFADialog) {
            this.showMFANotificationDialog();
            return;
        }
        const newDoc = new SupplierDocumentPropertyValues();
        this.openDialog(newDoc);
    }

    onEdit(model: SupplierDocumentPropertyValues): void {
        if (this.showMFADialog) {
            this.showMFANotificationDialog();
            return;
        }
        this.openDialog(model);
    }

    onDelete(item: SupplierDocumentPropertyValues): void {
        if (this.showMFADialog) {
            this.showMFANotificationDialog();
            return;
        }
        this.dialog
            .confirm(this.translateService.get(this.deleteTitle), this.translateService.get(this.deleteConfirmation))
            .afterClosed()
            .subscribe((res) => {
                if (res) {
                    item.operation.newValue = OPERATION.DELETE;
                    const properties = new SupplierPropertyValue();
                    properties.documents = [item];
                    this.postEvent(properties);
                }
            });
    }

    getCategory(type: string): string {
        const index = this.types.findIndex((t) => t.name === type);
        return index > -1 ? `${type} - ${this.types[index].description}` : '';
    }

    canChangeItem(item: SupplierDocumentPropertyValues): boolean {
        if (
            item &&
            (!(item.stageID && item.stageID.newValue) || (item.isTouched && item.stageID && item.stageID.newValue))
        ) {
            const keys = Object.keys(item);
            return (
                keys.some((key) => deepfind(item, `${key}.permission`) === 'ReadWrite') &&
                item.operation.newValue !== 'Delete'
            );
        }

        return false;
    }

    isExpired(item: SupplierDocumentPropertyValues): boolean {
        if (item.expiryDate && item.expiryDate.newValue) {
            const expiryDate = new Date(item.expiryDate.newValue);
            return expiryDate.getTime() < Date.now();
        }
        return false;
    }

    private postEvent(eventProperties: SupplierPropertyValue) {
        this.store.dispatch(
            new PostEvent({
                Name: 'Document Change Requested',
                Properties: this.pickNewValues(eventProperties),
                ReferenceIdentifier: this.supplierCode,
            }),
        );
        this.store.select(SelectEventData).subscribe((evt: any) => {
            if (evt) {
                this.loadTable(true);
            }
        });
    }

    private pickNewValues(supplierData: SupplierPropertyValue): Supplier {
        if (!supplierData) {
            return null;
        }

        const supplier = new Supplier();
        if (supplierData.documents) {
            supplier.Documents = [];
            supplierData.documents.forEach((document, index) => {
                supplier.Documents[index] = new SupplierDocument();
                Object.keys(document).forEach((name) => {
                    supplier.Documents[index][name] = getNestedNode([name, 'newValue'], document);
                });
                supplier.Documents[index].Operation = document.operation.newValue ? document.operation.newValue : null;
            });
        }
        return supplier;
    }

    showMFANotificationDialog() {
        const dialogRef = this.dialog.open(MfaBannerComponent, {
            width: '50%',
            data: { supplierCode: this.supplierCode, supplierContacts: this.supplierContacts },
        });

        dialogRef.afterClosed().subscribe((res) => {
            if (res) {
                window.location.reload();
            }
        });
    }

    private openDialog(model: SupplierDocumentPropertyValues): void {
        const dialogRef = this.matDialog.open(DocumentDialogComponent, {
            width: '68%',
            data: { supplierCode: this.supplierCode, address: 'test address', model: model },
        });

        dialogRef.afterClosed().subscribe((x) => {
            this.loadTable(true);
        });
    }

    private loadResources(): void {
        this.documentService.getDocumentType().subscribe((res) => {
            this.types = res;
            this.categories = Array.from(new Set(this.types.map((t) => t.category)))
                .sort()
                .map((x) => this.translateService.get(x));
        });
        this.store.dispatch(new GetTabPermissions(this.supplierCode));
        this.store.dispatch(new GetSupplierDocumentsListByCode(this.supplierCode));

        this.translateService.getAsync('Delete Item').subscribe((t) => (this.deleteTitle = t));
        this.translateService
            .getAsync('Are you sure you want to delete this item?')
            .subscribe((t) => (this.deleteConfirmation = t));
        this.store.select(SelectTabPermissions).subscribe((res) => (this.tabPermissions = res));
        this.loadTable();
    }

    private loadTable(reload: boolean = false): void {
        if (reload) {
            this.store.dispatch(new ClearSupplierDetailsByCode(this.supplierCode));
        }

        this.store.select(SelectSupplierDocumentList).subscribe((res) => {
            if (res) {
                this.allDocuments = res;
                this.total = res.length;
                this.onFilter();
            }
        });
    }
}

function deepfind(obj, path) {
    return path.split('.').reduce(function (o, x) {
        return o === undefined || o === null ? o : o[x];
    }, obj);
}